import { useState, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  Search,
  Upload,
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  Hash,
  Music2,
  Copy,
  Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOOKUP_OPTIONS = [
  { id: "both", label: "ISRC + ISWC", description: "Full lookup" },
  { id: "isrc_only", label: "ISRC Only", description: "Fast (~2s)" },
  { id: "iswc_only", label: "ISWC Only", description: "Slower" },
];

const CodeLookup = () => {
  const [input, setInput] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [iswcLoading, setIswcLoading] = useState(false);
  const [lookupType, setLookupType] = useState("both");
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const parseInput = useCallback((text) => {
    const lines = text.trim().split("\n").filter(Boolean);
    const tracks = [];
    for (const line of lines) {
      const parts = line.split(/[,\t]/).map((s) => s.trim().replace(/^["']|["']$/g, ""));
      if (parts.length >= 2) {
        tracks.push({ title: parts[0], artist: parts[1] });
      } else if (parts.length === 1 && parts[0].includes(" - ")) {
        const [artist, title] = parts[0].split(" - ").map((s) => s.trim());
        tracks.push({ title, artist });
      } else if (parts[0]) {
        tracks.push({ title: parts[0], artist: "" });
      }
    }
    return tracks;
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setInput(ev.target.result);
      toast.success(`Loaded ${file.name}`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleLookup = async () => {
    const tracks = parseInput(input);
    if (tracks.length === 0) {
      toast.error("No tracks found. Enter title, artist (one per line)");
      return;
    }
    if (tracks.length > 50) {
      toast.error("Maximum 50 tracks per lookup");
      return;
    }

    setLoading(true);
    setIswcLoading(false);
    setProgress(10);
    setResults(null);

    if (lookupType === "both") {
      // Phase 1: Fast ISRC lookup
      try {
        setProgress(20);
        const isrcRes = await axios.post(`${API}/lookup/codes/isrc`, { tracks });
        // Show ISRCs immediately, mark ISWCs as pending
        const entries = isrcRes.data.results.map((r) => ({
          ...r,
          iswc: null,
          iswc_source: null,
          iswc_status: "pending",
        }));
        setResults({ total: entries.length, found: isrcRes.data.found, results: entries, phase: "isrc_done" });
        setLoading(false);
        setIswcLoading(true);
        setProgress(50);

        // Phase 2+3: ISWC lookup in background
        const iswcTracks = entries.map((r) => ({
          title: r.title,
          artist: r.artist,
          isrc: r.isrc,
        }));
        
        try {
          const iswcRes = await axios.post(`${API}/lookup/codes/iswc`, { tracks: iswcTracks });

          // Merge ISWC results into existing entries
          const merged = entries.map((entry, i) => {
            const iswcEntry = iswcRes.data.results?.[i];
            const hasIswc = iswcEntry?.iswc;
            return {
              ...entry,
              iswc: hasIswc ? iswcEntry.iswc : null,
              iswc_source: hasIswc ? iswcEntry.iswc_source : null,
              iswc_status: hasIswc ? "found" : "not_found",
              status: (entry.isrc || hasIswc) ? "found" : entry.status === "skipped" ? "skipped" : "not_found",
            };
          });
          const foundCount = merged.filter((r) => r.status === "found").length;
          setResults({ total: merged.length, found: foundCount, results: merged, phase: "complete" });
          setProgress(100);
          toast.success(`Found codes for ${foundCount} of ${merged.length} tracks`);
        } catch (iswcError) {
          // ISWC failed but ISRCs are still valid
          const finalEntries = entries.map((e) => ({ ...e, iswc_status: "not_found" }));
          setResults((prev) => ({ ...prev, results: finalEntries, phase: "complete" }));
          toast.error("ISWC lookup failed, ISRCs are still available");
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Lookup failed");
      } finally {
        setIswcLoading(false);
      }
    } else {
      // Single-phase lookup (isrc_only or iswc_only)
      try {
        setProgress(30);
        const res = await axios.post(`${API}/lookup/codes`, { tracks, lookup_type: lookupType });
        setProgress(100);
        const entries = res.data.results.map((r) => ({
          ...r,
          iswc_status: lookupType === "isrc_only" ? "skipped" : (r.iswc ? "found" : "not_found"),
        }));
        setResults({ ...res.data, results: entries, phase: "complete" });
        toast.success(`Found codes for ${res.data.found} of ${res.data.total} tracks`);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Lookup failed");
      } finally {
        setLoading(false);
      }
    }
  };

  const exportCSV = () => {
    if (!results?.results) return;
    const header = "Title,Artist,ISRC,ISWC,Spotify Title,Spotify Artist,Status";
    const rows = results.results.map((r) =>
      [
        `"${(r.title || "").replace(/"/g, '""')}"`,
        `"${(r.artist || "").replace(/"/g, '""')}"`,
        r.isrc || "",
        r.iswc || "",
        `"${(r.spotify_title || "").replace(/"/g, '""')}"`,
        `"${(r.spotify_artist || "").replace(/"/g, '""')}"`,
        r.status || "",
      ].join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "isrc_iswc_lookup.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const trackCount = parseInput(input).length;

  return (
    <div className="space-y-6" data-testid="code-lookup-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-syncchain-text">ISRC / ISWC Lookup</h1>
        <p className="text-syncchain-text-secondary mt-1">
          Resolve ISRC and ISWC codes from track names — no full enrichment needed
        </p>
      </div>

      {/* Input Section */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardHeader>
          <CardTitle className="text-syncchain-text text-lg flex items-center gap-2">
            <Search className="w-5 h-5 text-syncchain-accent" />
            Track List
          </CardTitle>
          <CardDescription className="text-syncchain-text-muted">
            Enter one track per line: <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">Title, Artist</code> or{" "}
            <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">Artist - Title</code>. Max 50 tracks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Ferrari, James Hype\nCatch My Love, Noizu\nBicep - Glue\nAll For Us, Labrinth`}
            className="min-h-[140px] bg-syncchain-surface border-white/10 text-syncchain-text font-mono text-sm resize-y"
            data-testid="code-lookup-input"
          />

          {/* Lookup Type Toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-syncchain-text-muted mr-1">Lookup:</span>
            {LOOKUP_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setLookupType(opt.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  lookupType === opt.id
                    ? "bg-syncchain-accent text-white"
                    : "bg-white/5 text-syncchain-text-muted hover:bg-white/10 hover:text-syncchain-text-secondary"
                }`}
                data-testid={`lookup-type-${opt.id}`}
              >
                {opt.label}
                <span className="ml-1 opacity-60">{opt.description}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleLookup}
              disabled={loading || iswcLoading || !input.trim()}
              className="bg-syncchain-accent hover:bg-syncchain-accent-hover text-white rounded-full px-6"
              data-testid="code-lookup-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Looking up...</>
              ) : (
                <><Search className="w-4 h-4 mr-2" />Look Up Codes</>
              )}
            </Button>

            <input type="file" accept=".csv,.txt" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="border-white/10 text-syncchain-text-secondary"
              data-testid="code-lookup-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />Upload CSV
            </Button>

            {input.trim() && (
              <Button variant="ghost" size="sm" onClick={() => { setInput(""); setResults(null); }} className="text-syncchain-text-muted">
                <Trash2 className="w-4 h-4 mr-1" />Clear
              </Button>
            )}

            <span className="text-xs text-syncchain-text-muted ml-auto">
              {trackCount} track{trackCount !== 1 ? "s" : ""}
            </span>
          </div>

          {(loading || iswcLoading) && <Progress value={progress} className="h-1.5 [&>div]:bg-syncchain-accent" />}
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card className="bg-syncchain-paper border-white/10" data-testid="code-lookup-results">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-syncchain-text text-lg flex items-center gap-2">
                <Hash className="w-5 h-5 text-syncchain-accent" />
                Results
                <Badge variant="outline" className="ml-2 text-syncchain-accent border-syncchain-accent/30">
                  {results.found}/{results.total} found
                </Badge>
                {iswcLoading && (
                  <Badge variant="outline" className="ml-1 text-purple-400 border-purple-400/30 animate-pulse">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Resolving ISWCs...
                  </Badge>
                )}
              </CardTitle>
              <Button
                onClick={exportCSV}
                variant="outline"
                className="border-white/10 text-syncchain-text-secondary"
                disabled={iswcLoading}
                data-testid="code-lookup-export-btn"
              >
                <Download className="w-4 h-4 mr-2" />Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="code-lookup-table">
                <thead>
                  <tr className="border-b border-white/10 text-syncchain-text-muted text-xs uppercase tracking-wider">
                    <th className="py-3 pr-4 text-left w-8">#</th>
                    <th className="py-3 pr-4 text-left">Track</th>
                    <th className="py-3 pr-4 text-left">Artist</th>
                    {lookupType !== "iswc_only" && <th className="py-3 pr-4 text-left">ISRC</th>}
                    {lookupType !== "isrc_only" && <th className="py-3 pr-4 text-left">ISWC</th>}
                    <th className="py-3 text-left w-10">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((r, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 pr-4 text-syncchain-text-muted text-xs">{i + 1}</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <Music2 className="w-3.5 h-3.5 text-syncchain-text-muted flex-shrink-0" />
                          <span className="text-syncchain-text font-medium truncate max-w-[200px]">
                            {r.spotify_title || r.title}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-syncchain-text-secondary truncate max-w-[180px]">
                        {r.spotify_artist || r.artist}
                      </td>
                      {lookupType !== "iswc_only" && (
                        <td className="py-3 pr-4">
                          {r.isrc ? (
                            <button
                              onClick={() => copyToClipboard(r.isrc)}
                              className="font-mono text-xs text-syncchain-accent hover:text-syncchain-accent-hover transition-colors flex items-center gap-1"
                              title="Click to copy"
                            >
                              {r.isrc}
                              <Copy className="w-3 h-3 opacity-50" />
                            </button>
                          ) : (
                            <span className="text-syncchain-text-muted text-xs">—</span>
                          )}
                        </td>
                      )}
                      {lookupType !== "isrc_only" && (
                        <td className="py-3 pr-4">
                          {r.iswc ? (
                            <button
                              onClick={() => copyToClipboard(r.iswc)}
                              className="font-mono text-xs text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
                              title="Click to copy"
                            >
                              {r.iswc}
                              <Copy className="w-3 h-3 opacity-50" />
                            </button>
                          ) : r.iswc_status === "pending" ? (
                            <span className="flex items-center gap-1.5 text-xs text-purple-400">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Finding...
                            </span>
                          ) : (
                            <span className="text-syncchain-text-muted text-xs">—</span>
                          )}
                        </td>
                      )}
                      <td className="py-3">
                        {r.status === "found" ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : r.status === "not_found" && !iswcLoading ? (
                          <XCircle className="w-4 h-4 text-amber-400" />
                        ) : r.status === "skipped" ? (
                          <span className="text-xs text-syncchain-text-muted">—</span>
                        ) : (
                          <Loader2 className="w-4 h-4 text-syncchain-text-muted animate-spin" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* How it works */}
      <Card className="bg-syncchain-surface border-white/5">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-syncchain-text mb-3">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-syncchain-text-secondary">
            <div className="flex gap-2">
              <span className="text-syncchain-accent font-bold">1.</span>
              <span>Enter track titles and artist names (one per line, comma or tab separated)</span>
            </div>
            <div className="flex gap-2">
              <span className="text-syncchain-accent font-bold">2.</span>
              <span>ISRCs are resolved instantly. ISWCs are resolved via industry databases and PROs.</span>
            </div>
            <div className="flex gap-2">
              <span className="text-syncchain-accent font-bold">3.</span>
              <span>Export results as CSV with all matched codes. Click any code to copy it.</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CodeLookup;
